import React, { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { 
  Users as UsersIcon, 
  UserPlus, 
  Shield, 
  MoreHorizontal, 
  Mail, 
  Building2,
  CheckCircle2,
  XCircle,
  Edit2,
  Save,
  Plus,
  Loader2,
  AlertCircle,
  Eye,
  Key,
  X,
  Download,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/utils/trpc";
import NewUserForm from "./NewUserForm";
import ViewUserDialog from "./ViewUserDialog";
import EditUserDialog from "./EditUserDialog";
import {
  FioriPageHeader,
  FioriFilterBar,
  FioriTable,
  FioriStatusBadge,
} from "@/components/fiori";
import type { FioriColumn } from "@/components/fiori";

export default function Users() {
  const { t } = useTranslation();

  // Permission modules — labels translated inside component so they react to language changes
  const permissionModules = useMemo(() => [
    { 
      id: "dashboard", 
      label: t("users.permissions.modules.dashboard"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewDashboard") }
      ] 
    },
    { 
      id: "requests", 
      label: t("users.permissions.modules.requests"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewAllRequests") },
        { id: "create", label: t("users.permissions.actions.createRequest") },
        { id: "update", label: t("users.permissions.actions.editRequest") },
        { id: "delete", label: t("users.permissions.actions.deleteRequest") }
      ] 
    },
    { 
      id: "approvals", 
      label: t("users.permissions.modules.approvals"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewApprovals") },
        { id: "l1", label: t("users.permissions.actions.l1Approval") },
        { id: "manual", label: t("users.permissions.actions.manualApproval") }
      ] 
    },
    { 
      id: "sites", 
      label: t("users.permissions.modules.siteManagement"), 
      actions: [
        { id: "create", label: t("users.permissions.actions.createSites") },
        { id: "read", label: t("users.permissions.actions.viewSites") },
        { id: "update", label: t("users.permissions.actions.updateSites") },
        { id: "delete", label: t("users.permissions.actions.deleteSites") }
      ] 
    },
    { 
      id: "zones", 
      label: t("users.permissions.modules.zoneManagement"), 
      actions: [
        { id: "create", label: t("users.permissions.actions.createZones") },
        { id: "read", label: t("users.permissions.actions.viewZones") },
        { id: "update", label: t("users.permissions.actions.updateZones") },
        { id: "lock", label: t("users.permissions.actions.lockUnlockZones") }
      ] 
    },
    { 
      id: "areas", 
      label: t("users.permissions.modules.areaManagement"), 
      actions: [
        { id: "create", label: t("users.permissions.actions.createAreas") },
        { id: "read", label: t("users.permissions.actions.viewAreas") },
        { id: "update", label: t("users.permissions.actions.updateAreas") },
        { id: "delete", label: t("users.permissions.actions.deleteAreas") }
      ] 
    },
    { 
      id: "alerts", 
      label: t("users.permissions.modules.securityAlerts"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewAlerts") },
        { id: "resolve", label: t("users.permissions.actions.resolveAlerts") }
      ] 
    },
    { 
      id: "users", 
      label: t("users.permissions.modules.userAdministration"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewUsers") },
        { id: "create", label: t("users.permissions.actions.createUser") },
        { id: "update", label: t("users.permissions.actions.editUser") },
        { id: "delete", label: t("users.permissions.actions.deleteUser") },
        { id: "manage_roles", label: t("users.permissions.actions.manageUserRoles") }
      ] 
    },
    { 
      id: "groups", 
      label: t("users.permissions.modules.groups"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewGroups") },
        { id: "create", label: t("users.permissions.actions.createGroup") },
        { id: "update", label: t("users.permissions.actions.editGroup") },
        { id: "delete", label: t("users.permissions.actions.deleteGroup") }
      ] 
    },
    { 
      id: "workflows", 
      label: t("users.permissions.modules.workflowManagement"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewWorkflows") },
        { id: "create", label: t("users.permissions.actions.createWorkflow") },
        { id: "update", label: t("users.permissions.actions.editWorkflow") },
        { id: "delete", label: t("users.permissions.actions.deleteWorkflow") }
      ] 
    },
    { 
      id: "requestTypes", 
      label: t("users.permissions.modules.requestTypes"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewRequestTypes") },
        { id: "create", label: t("users.permissions.actions.createRequestType") },
        { id: "update", label: t("users.permissions.actions.editRequestType") },
        { id: "delete", label: t("users.permissions.actions.deleteRequestType") }
      ] 
    },
    { 
      id: "shifts", 
      label: t("users.permissions.modules.shiftManagement"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewShifts") },
        { id: "create", label: t("users.permissions.actions.createShift") },
        { id: "update", label: t("users.permissions.actions.editShift") },
        { id: "delete", label: t("users.permissions.actions.deleteShift") }
      ] 
    },
    { 
      id: "delegations", 
      label: t("users.permissions.modules.delegations"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewDelegations") },
        { id: "create", label: t("users.permissions.actions.createDelegation") },
        { id: "update", label: t("users.permissions.actions.editDelegation") },
        { id: "delete", label: t("users.permissions.actions.deleteDelegation") }
      ] 
    },
    { 
      id: "cards", 
      label: t("users.permissions.modules.cardManagement"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewCards") },
        { id: "issue", label: t("users.permissions.actions.issueCard") },
        { id: "revoke", label: t("users.permissions.actions.revokeCard") },
        { id: "control", label: t("users.permissions.actions.controlCard") }
      ] 
    },
    { 
      id: "hardware", 
      label: t("users.permissions.modules.hardware"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewHardware") },
        { id: "control", label: t("users.permissions.actions.controlHardware") }
      ] 
    },
    { 
      id: "analytics", 
      label: t("users.permissions.modules.analyticsReports"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewAnalytics") },
        { id: "create", label: t("users.permissions.actions.createReports") },
        { id: "export", label: t("users.permissions.actions.exportAnalytics") }
      ] 
    },
    { 
      id: "reports", 
      label: t("users.permissions.modules.reports"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewReports") },
        { id: "export", label: t("users.permissions.actions.exportReports") }
      ] 
    },
    { 
      id: "settings", 
      label: t("users.permissions.modules.settings"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewSettings") },
        { id: "update", label: t("users.permissions.actions.updateSettings") }
      ] 
    },
    { 
      id: "integrations", 
      label: t("users.permissions.modules.integrationHub"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewIntegrations") },
        { id: "configure", label: t("users.permissions.actions.configureIntegrations") }
      ] 
    },
    { 
      id: "admin", 
      label: t("users.permissions.modules.admin"), 
      actions: [
        { id: "access", label: t("users.permissions.actions.accessAdminPanel") },
        { id: "roles", label: t("users.permissions.actions.manageRoles") },
        { id: "audit", label: t("users.permissions.actions.viewAuditLogs") }
      ] 
    },
    { 
      id: "facilities", 
      label: t("users.permissions.modules.facilities"), 
      actions: [
        { id: "view", label: t("users.permissions.actions.viewFacilities") },
        { id: "create", label: t("users.permissions.actions.createFacilities") },
        { id: "update", label: t("users.permissions.actions.updateFacilities") }
      ] 
    }
  ], [t]);

  const [searchTerm, setSearchTerm] = useState("");
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [viewUserOpen, setViewUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editUserRoleOpen, setEditUserRoleOpen] = useState(false);
  const [editUserRoleUser, setEditUserRoleUser] = useState<any>(null);
  const [selectedRoleValue, setSelectedRoleValue] = useState<string>("");
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [roleFilter, setRoleFilter] = useState<"all" | "user" | "admin">("all");
  const [departmentFilter, setDepartmentFilter] = useState<number | null>(null);
  const [groupFilter, setGroupFilter] = useState<number | null>(null);
  
  // Change password dialog state
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePasswordUser, setChangePasswordUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Delete user dialog state
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState<any>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Pending permissions ref to avoid stale closure in onSuccess
  const pendingPermissionsRef = useRef<{ roleId: number; codes: string[] } | null>(null);

  // Queries
  const { data: usersData, isLoading: usersLoading, isFetching: usersFetching, error: usersError, refetch: refetchUsers } = trpc.users.list.useQuery({
    search: searchTerm || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    role: roleFilter !== "all" ? roleFilter : undefined,
    departmentId: departmentFilter || undefined,
    groupId: groupFilter || undefined,
  });

  const { data: rolesData, isLoading: rolesLoading, isFetching: rolesFetching, error: rolesError, refetch: refetchRoles } = trpc.roles.list.useQuery({});
  const utils = trpc.useUtils();
  const { data: systemRolesData } = trpc.users.getSystemRoles.useQuery();
  const { data: departmentsData } = trpc.departments.list.useQuery({});
  const { data: groupsData } = trpc.groups.list.useQuery({});

  const users = usersData?.users || [];
  const filteredUsers = users;
  const roles = rolesData || [];
  const departments = departmentsData || [];
  const groups = groupsData || [];

  // Mutations
  const assignRoleMutation = trpc.users.assignRole.useMutation({
    onSuccess: () => {
      toast.success(t("users.roleAssigned"));
      refetchUsers();
      setEditUserRoleOpen(false);
    },
    onError: (error: any) => {
      toast.error(t("users.roleAssignFailed"), { description: error.message });
    },
  });

  const changePasswordMutation = trpc.users.changePassword.useMutation({
    onSuccess: () => {
      toast.success(t("users.passwordChanged"));
      setChangePasswordOpen(false);
      setChangePasswordUser(null);
      setNewPassword("");
      setConfirmPassword("");
    },
    onError: (error: any) => {
      toast.error(t("users.passwordChangeFailed"), { description: error.message });
    },
  });

  const activateMutation = trpc.users.activate.useMutation({
    onSuccess: () => {
      toast.success(t("users.userActivated"));
      refetchUsers();
    },
    onError: (error: any) => {
      toast.error(t("users.activateFailed"), { description: error.message });
    },
  });

  const deactivateMutation = trpc.users.deactivate.useMutation({
    onSuccess: () => {
      toast.success(t("users.userDeactivated"));
      refetchUsers();
    },
    onError: (error: any) => {
      toast.error(t("users.deactivateFailed"), { description: error.message });
    },
  });

  const deleteUserMutation = trpc.users.delete.useMutation({
    onSuccess: () => {
      toast.success(t("users.userDeleted"));
      refetchUsers();
      setDeleteUserOpen(false);
      setDeleteUserTarget(null);
      setDeleteConfirmText("");
    },
    onError: (error: any) => {
      toast.error(t("users.deleteFailed"), { description: error.message });
    },
  });

  const createRoleMutation = trpc.roles.create.useMutation({
    onSuccess: async (newRole: any) => {
      toast.success(t("users.roleCreated"));
      await refetchRoles();
      if (pendingPermissionsRef.current) {
        updatePermissionsMutation.mutate({ roleId: newRole.id, permissions: pendingPermissionsRef.current.codes as string[] });
        pendingPermissionsRef.current = null;
      }
      setEditRoleOpen(false);
    },
    onError: (error: any) => {
      toast.error(t("users.roleCreateFailed"), { description: error.message });
    },
  });

  const updateRoleMutation = trpc.roles.update.useMutation({
    onSuccess: async () => {
      if (pendingPermissionsRef.current) {
        updatePermissionsMutation.mutate({ roleId: pendingPermissionsRef.current.roleId, permissions: pendingPermissionsRef.current.codes as string[] });
        pendingPermissionsRef.current = null;
      } else {
        toast.success(t("users.roleUpdated"));
        await refetchRoles();
        setEditRoleOpen(false);
      }
    },
    onError: (error: any) => {
      toast.error(t("users.roleUpdateFailed"), { description: error.message });
    },
  });

  const updatePermissionsMutation = trpc.roles.updatePermissions.useMutation({
    onSuccess: async () => {
      toast.success(t("users.roleUpdated"));
      await refetchRoles();
      setEditRoleOpen(false);
    },
    onError: (error: any) => {
      toast.error(t("users.permissionsUpdateFailed"), { description: error.message });
    },
  });

  const handleViewUser = (user: any) => {
    setSelectedUser(user);
    setViewUserOpen(true);
  };

  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setEditUserOpen(true);
  };

  const handleDeleteUser = (user: any) => {
    setDeleteUserTarget(user);
    setDeleteConfirmText("");
    setDeleteUserOpen(true);
  };

  const handleConfirmDeleteUser = () => {
    if (deleteUserTarget && deleteConfirmText === "DELETE") {
      deleteUserMutation.mutate({ id: deleteUserTarget.id });
    }
  };

  const handleCreateRole = () => {
    setSelectedRole({ isNew: true, name: "", description: "", permissions: {}, mobileRole: "employee" });
    setEditRoleOpen(true);
  };

  const handleEditRole = (role: any) => {
    setSelectedRole({ ...role, isNew: false });
    setEditRoleOpen(true);
  };

  const handleSaveRole = () => {
    if (!selectedRole?.name) { toast.error(t("users.roleNameRequired")); return; }
    // Convert nested permissions object to flat array of "module:action" codes
    const permissionCodesList: string[] = [];
    permissionModules.forEach(m => {
      const modulePerms = selectedRole.permissions?.[m.id] || {};
      m.actions.forEach(a => {
        if (modulePerms[a.id]) permissionCodesList.push(`${m.id}:${a.id}`);
      });
    });
    if (selectedRole.isNew) {
      const code = selectedRole.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      createRoleMutation.mutate({
        name: selectedRole.name, code,
        description: selectedRole.description, level: 50, permissions: permissionCodesList,
        mobileRole: (selectedRole.mobileRole as any) || 'employee',
      });
    } else {
      if (selectedRole.id) {
        pendingPermissionsRef.current = { roleId: selectedRole.id, codes: permissionCodesList as any };
      }
      updateRoleMutation.mutate({
        id: selectedRole.id, name: selectedRole.name,
        description: selectedRole.description,
        mobileRole: (selectedRole.mobileRole as any) || 'employee',
      });
    }
  };

  const handleEmailUser = (user: any) => {
    if (user.email) window.location.href = `mailto:${user.email}`;
    else toast.error(t("users.noEmailAddress"));
  };

  const handleChangePassword = (user: any) => {
    setChangePasswordUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setChangePasswordOpen(true);
  };

  const handleSubmitPasswordChange = () => {
    if (!changePasswordUser) return;
    if (newPassword.length < 6) { toast.error(t("users.passwordMinLength")); return; }
    if (newPassword !== confirmPassword) { toast.error(t("users.passwordMismatch")); return; }
    changePasswordMutation.mutate({ userId: changePasswordUser.id, newPassword });
  };

  const handleActivateUser = (user: any) => activateMutation.mutate({ id: user.id });
  const handleDeactivateUser = (user: any) => deactivateMutation.mutate({ id: user.id });

  const clearFilters = () => {
    setStatusFilter("all");
    setRoleFilter("all");
    setDepartmentFilter(null);
    setGroupFilter(null);
  };

  const hasActiveFilters = statusFilter !== "all" || roleFilter !== "all" || departmentFilter !== null || groupFilter !== null;

  const formatDate = (date: Date | string | null) => {
    if (!date) return t("users.never");
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMs < 60000) return t("users.justNow");
    if (diffHours < 1) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-US", { year: "numeric", month: "numeric", day: "numeric" });
  };

  const getUserDisplayName = (user: any) => {
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    return user.name || t("users.unknown");
  };

  const getUserInitials = (user: any) => {
    if (user.firstName && user.lastName) return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    if (user.name) {
      const parts = user.name.split(" ");
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      return user.name[0]?.toUpperCase() || "U";
    }
    return "U";
  };

  const getDepartmentName = (departmentId: number | null) => {
    if (!departmentId) return "\u2014";
    const dept = departments.find((d: any) => d.id === departmentId);
    return dept?.name || "\u2014";
  };

  // Build active filter chips
  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (statusFilter !== "all") chips.push({ key: "status", label: `${t("common.status")}: ${statusFilter}`, onRemove: () => setStatusFilter("all") });
    if (roleFilter !== "all") chips.push({ key: "role", label: `${t("users.systemRole")}: ${roleFilter}`, onRemove: () => setRoleFilter("all") });
    if (departmentFilter) chips.push({ key: "dept", label: `${t("users.department")}: ${getDepartmentName(departmentFilter)}`, onRemove: () => setDepartmentFilter(null) });
    if (groupFilter) chips.push({ key: "group", label: `${t("common.group")}: ${groups.find((g: any) => g.id === groupFilter)?.name || groupFilter}`, onRemove: () => setGroupFilter(null) });
    return chips;
  }, [statusFilter, roleFilter, departmentFilter, groupFilter, departments, groups, t]);

  // Table columns definition
  const userColumns: FioriColumn<any>[] = useMemo(() => [
    {
      key: "user",
      header: t("users.user"),
      width: "220px",
      render: (user: any) => (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarFallback className="bg-[#E8DCF5] text-[#5B2C93] text-xs font-medium">
              {getUserInitials(user)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-[#2C2C2C] truncate">{getUserDisplayName(user)}</p>
            <p className="text-xs text-[#6B6B6B] truncate">{user.email || t("users.noEmail")}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: t("users.systemRole"),
      width: "150px",
      render: (user: any) => (
        <FioriStatusBadge
          status="info"
          label={(user as any).systemRole?.name || (user.role === "admin" ? t("users.administrator") : t("users.requestor"))}
          showDot={false}
        />
      ),
    },
    {
      key: "department",
      header: t("users.department"),
      width: "160px",
      render: (user: any) => (
        <div className="flex items-center gap-1.5 text-sm text-[#6B6B6B] min-w-0">
          <Building2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{getDepartmentName(user.departmentId)}</span>
        </div>
      ),
    },
    {
      key: "status",
      header: t("common.status"),
      width: "100px",
      render: (user: any) => (
        <FioriStatusBadge status={(user as any).status || "active"} />
      ),
    },
    {
      key: "lastActive",
      header: t("users.lastActive"),
      width: "120px",
      render: (user: any) => (
        <span className="text-sm text-[#6B6B6B] whitespace-nowrap">{formatDate(user.lastSignedIn)}</span>
      ),
    },
    {
      key: "actions",
      header: t("common.actions"),
      align: "end" as const,
      width: "80px",
      render: (user: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs text-[#6B6B6B]">{t("common.actions")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewUser(user); }}>
              <Eye className="h-4 w-4 mr-2" /> {t("users.viewDetails")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { 
              e.stopPropagation(); 
              setEditUserRoleUser(user);
              setSelectedRoleValue((user as any).systemRole?.code || "");
              setEditUserRoleOpen(true);
            }}>
              <Edit2 className="h-4 w-4 mr-2" /> {t("users.changeRole")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEmailUser(user); }}>
              <Mail className="h-4 w-4 mr-2" /> {t("users.emailUser")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleChangePassword(user); }}>
              <Key className="h-4 w-4 mr-2" /> {t("users.changePassword")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-[#6B6B6B] font-normal">{t("users.changeStatus")}</DropdownMenuLabel>
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); handleActivateUser(user); }}
              className="text-[#059669]"
              disabled={(user as any).status === "active"}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> {t("common.activate")}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); handleDeactivateUser(user); }}
              className="text-[#FF6B6B]"
              disabled={(user as any).status === "inactive"}
            >
              <XCircle className="h-4 w-4 mr-2" /> {t("common.deactivate")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); handleDeleteUser(user); }}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" /> {t("users.deleteUser")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [departments, groups, t]);

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      {/* SAP Fiori Page Header */}
      <FioriPageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        icon={<UsersIcon className="h-5 w-5" />}
        count={filteredUsers.length}
        onRefresh={() => { refetchUsers(); refetchRoles(); }}
        isRefreshing={usersFetching || rolesFetching}
        actions={
          <Button onClick={() => setNewUserOpen(true)} className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-2" size="sm">
            <UserPlus className="h-4 w-4" /> {t("users.addUser")}
          </Button>
        }
      />

      {/* Add User Dialog */}
      <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
        <DialogContent className="sm:max-w-[900px] h-[80vh] p-0 overflow-hidden" showCloseButton={false}>
          <NewUserForm
            onSuccess={() => { setNewUserOpen(false); refetchUsers(); }}
            onCancel={() => setNewUserOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t("users.changePassword")}</DialogTitle>
            <DialogDescription>
              {t("users.setNewPasswordFor")} {changePasswordUser?.name || changePasswordUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t("users.newPassword")}</Label>
              <Input id="newPassword" type="password" placeholder="••••••" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} minLength={6} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("users.confirmPassword")}</Label>
              <Input id="confirmPassword" type="password" placeholder="••••••" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} minLength={6} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bg-[#5B2C93] hover:bg-[#3D1C5E]" disabled={changePasswordMutation.isPending}
              onClick={handleSubmitPasswordChange}>
              {changePasswordMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("users.changePassword")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabbed Content */}
      <Tabs defaultValue="users" className="w-full">
        <div className="bg-white border-b border-[#E0E0E0] px-0">
          <TabsList className="bg-transparent h-auto p-0 gap-0">
            <TabsTrigger value="users" className="rounded-none border-0 shadow-none bg-transparent border-b-2 border-b-transparent data-[state=active]:border-b-[#5B2C93] data-[state=active]:bg-transparent data-[state=active]:text-[#5B2C93] data-[state=active]:shadow-none px-4 py-3 text-sm font-medium text-[#6B6B6B]">
              {t("users.usersDirectory")}
            </TabsTrigger>
            <TabsTrigger value="roles" className="rounded-none border-0 shadow-none bg-transparent border-b-2 border-b-transparent data-[state=active]:border-b-[#5B2C93] data-[state=active]:bg-transparent data-[state=active]:text-[#5B2C93] data-[state=active]:shadow-none px-4 py-3 text-sm font-medium text-[#6B6B6B]">
              {t("users.rolesPermissions")}
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Users Directory Tab */}
        <TabsContent value="users" className="mt-5 space-y-4">
          {/* SAP Fiori Filter Bar */}
          <FioriFilterBar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder={t("users.searchPlaceholder")}
            activeFilters={activeFilterChips}
            onClearAll={clearFilters}
            filters={
              <>
                <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
                  <SelectTrigger className="w-full sm:w-[140px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0]">
                    <SelectValue placeholder={t("common.status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("users.allStatuses")}</SelectItem>
                    <SelectItem value="active">{t("common.active")}</SelectItem>
                    <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={roleFilter} onValueChange={(v: any) => setRoleFilter(v)}>
                  <SelectTrigger className="w-full sm:w-[140px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0]">
                    <SelectValue placeholder={t("users.role")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("users.allRoles")}</SelectItem>
                    <SelectItem value="admin">{t("users.admin")}</SelectItem>
                    <SelectItem value="user">{t("users.userRole")}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={departmentFilter?.toString() || "all"} onValueChange={(v) => setDepartmentFilter(v === "all" ? null : parseInt(v))}>
                  <SelectTrigger className="w-full sm:w-[160px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0]">
                    <SelectValue placeholder={t("users.department")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("users.allDepartments")}</SelectItem>
                    {departments.map((dept: any) => (
                      <SelectItem key={dept.id} value={dept.id.toString()}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            trailing={
              <Button variant="outline" size="sm" className="gap-1.5 text-[#6B6B6B] border-[#E0E0E0]" onClick={() => toast.info(t("common.exportComingSoon"))}>
                <Download className="h-3.5 w-3.5" /> {t("common.export")}
              </Button>
            }
          />

          {/* SAP Fiori Table */}
          <FioriTable
            columns={userColumns}
            data={filteredUsers}
            isLoading={usersLoading}
            rowKey={(user: any) => user.id}
            onRowClick={handleViewUser}
            emptyIcon={<UsersIcon className="h-10 w-10" />}
            emptyTitle={t("users.noUsersFound")}
            emptyDescription={t("users.noUsersDescription")}
            footerInfo={t("users.showingOf", { shown: filteredUsers.length, total: users.length })}
          />
        </TabsContent>

        {/* Roles & Permissions Tab */}
        <TabsContent value="roles" className="mt-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#6B6B6B]">
              {t("users.defineRolesDescription")}
            </p>
            <Button onClick={handleCreateRole} className="gap-2 bg-[#5B2C93] hover:bg-[#3D1C5E]" size="sm">
              <Plus className="h-4 w-4" /> {t("users.createRole")}
            </Button>
          </div>

          {rolesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-7 w-7 animate-spin text-[#5B2C93]" />
              <span className="ml-3 text-sm text-[#6B6B6B]">{t("users.loadingRoles")}</span>
            </div>
          ) : rolesError ? (
            <div className="flex items-center justify-center py-12 text-[#FF6B6B]">
              <AlertCircle className="h-6 w-6 mr-2" />
              <span>{t("users.errorLoadingRoles")}: {rolesError.message}</span>
            </div>
          ) : roles.length === 0 ? (
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-8 text-center">
              <Shield className="h-12 w-12 mx-auto mb-4 text-[#B0B0B0]" />
              <p className="text-base font-medium text-[#2C2C2C]">{t("users.noCustomRoles")}</p>
              <p className="text-sm text-[#6B6B6B] mb-4">{t("users.noCustomRolesDescription")}</p>
              <Button onClick={handleCreateRole} className="gap-2 bg-[#5B2C93] hover:bg-[#3D1C5E]" size="sm">
                <Plus className="h-4 w-4" /> {t("users.createFirstRole")}
              </Button>
            </div>
          ) : (
            <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
              {/* Header row */}
              <div className="grid grid-cols-[2fr_3fr_1fr_1fr_auto] gap-4 px-5 py-3 bg-[#FAFAFA] border-b border-[#E0E0E0] text-xs font-semibold text-[#6B6B6B] uppercase tracking-wide">
                <span>{t("users.roleName")}</span>
                <span>{t("users.permissions")}</span>
                <span>{t("users.mobileRole")}</span>
                <span>{t("users.usersCount")}</span>
                <span></span>
              </div>
              {roles.map((role: any, idx: number) => {
                const permKeys = Object.entries(role.permissions || {} as Record<string, unknown>)
                  .filter(([_, value]) => value && typeof value === 'object' && Object.values(value as Record<string, boolean>).some(Boolean))
                  .map(([key]) => key);
                return (
                  <div
                    key={role.id}
                    className={`grid grid-cols-[2fr_3fr_1fr_1fr_auto] gap-4 px-5 py-4 items-center hover:bg-[#F9F7FF] transition-colors cursor-pointer ${idx !== roles.length - 1 ? 'border-b border-[#E0E0E0]' : ''}`}
                    onClick={() => handleEditRole(role)}
                  >
                    {/* Name + description */}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#2C2C2C] truncate">{role.name}</p>
                      <p className="text-xs text-[#6B6B6B] truncate mt-0.5">{role.description || t("users.noDescription")}</p>
                    </div>
                    {/* Permission badges */}
                    <div className="flex flex-wrap gap-1">
                      {permKeys.slice(0, 4).map((key) => (
                        <Badge key={key} variant="secondary" className="text-xs bg-[#F5F5F5] text-[#6B6B6B]">{key}</Badge>
                      ))}
                      {permKeys.length > 4 && (
                        <Badge variant="secondary" className="text-xs bg-[#F5F5F5] text-[#6B6B6B]">+{permKeys.length - 4} {t("common.more")}</Badge>
                      )}
                    </div>
                    {/* Mobile Role */}
                    <div>
                      {role.mobileRole === 'security' && <Badge variant="secondary" className="text-xs bg-[#FFF3E0] text-[#E65100] border-[#FFB74D]">{t("users.mobileRoles.security")}</Badge>}
                      {role.mobileRole === 'manager' && <Badge variant="secondary" className="text-xs bg-[#E8F5E9] text-[#2E7D32] border-[#81C784]">{t("users.mobileRoles.manager")}</Badge>}
                      {role.mobileRole === 'visitor' && <Badge variant="secondary" className="text-xs bg-[#F3E5F5] text-[#6A1B9A] border-[#CE93D8]">{t("users.mobileRoles.visitor")}</Badge>}
                      {(!role.mobileRole || role.mobileRole === 'employee') && <Badge variant="secondary" className="text-xs bg-[#E3F2FD] text-[#1565C0] border-[#90CAF9]">{t("users.mobileRoles.employee")}</Badge>}
                    </div>
                    {/* User count */}
                    <div>
                      <FioriStatusBadge status="info" label={`${role.userCount || 0} ${t("users.usersCount")}`} showDot={false} />
                    </div>
                    {/* Edit chevron */}
                    <div className="text-[#B0B0B0]">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* View User Dialog */}
      <ViewUserDialog
        user={selectedUser}
        open={viewUserOpen}
        onOpenChange={setViewUserOpen}
        onEdit={() => { setViewUserOpen(false); setEditUserOpen(true); }}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        user={selectedUser}
        open={editUserOpen}
        onOpenChange={setEditUserOpen}
        onSuccess={() => refetchUsers()}
      />

      {/* Edit User Role Dialog */}
      <Dialog open={editUserRoleOpen} onOpenChange={setEditUserRoleOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#5B2C93]" />
              {t("users.assignSystemRole")}
            </DialogTitle>
            <DialogDescription>
              {t("users.changeRoleFor")} <span className="font-medium">{editUserRoleUser?.name || (editUserRoleUser?.firstName ? editUserRoleUser.firstName + ' ' + editUserRoleUser.lastName : editUserRoleUser?.email)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>{t("users.currentRole")}</Label>
              <div className="p-3 bg-[#FAFAFA] border border-[#E0E0E0] rounded-lg">
                <span className="font-medium text-sm">{editUserRoleUser?.systemRole?.name || t("users.noRoleAssigned")}</span>
                {editUserRoleUser?.systemRole?.description && (
                  <p className="text-xs text-[#6B6B6B] mt-1">{editUserRoleUser?.systemRole?.description}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("users.newSystemRole")}</Label>
              <Select value={selectedRoleValue} onValueChange={setSelectedRoleValue}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder={t("users.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  {(systemRolesData || []).map((role: any) => (
                    <SelectItem key={role.code} value={role.code}>
                      <div className="flex flex-col">
                        <span className="font-medium">{role.name}</span>
                        <span className="text-xs text-[#6B6B6B]">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedRoleValue && (
              <div className="p-3 bg-[#E8DCF5] border border-[#5B2C93] rounded-lg">
                <p className="text-sm text-[#5B2C93]">
                  <strong>{t("common.note")}:</strong> {t("users.roleChangeNote")}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserRoleOpen(false)}>{t("common.cancel")}</Button>
            <Button 
              className="bg-[#5B2C93] hover:bg-[#3D1C5E]"
              disabled={assignRoleMutation.isPending || !selectedRoleValue}
              onClick={() => {
                if (editUserRoleUser && selectedRoleValue) {
                  assignRoleMutation.mutate({ userId: editUserRoleUser.id, roleCode: selectedRoleValue });
                }
              }}
            >
              {assignRoleMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("users.assignRole")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog - SAP-style Authorization Management */}
      <Dialog open={editRoleOpen} onOpenChange={setEditRoleOpen}>
        <DialogContent className="w-[95vw] sm:max-w-[900px] max-h-[85vh] overflow-hidden flex flex-col p-0">
          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-[#E0E0E0] bg-white">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-lg font-medium text-[#2C2C2C]">
                  {selectedRole?.isNew ? t("users.createNewRole") : t("users.editRole")}
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm">
                  {t("users.configureRoleDescription")}
                </DialogDescription>
              </div>
              <FioriStatusBadge status="info" label={selectedRole?.isNew ? t("common.create") : t("common.edit")} showDot={false} />
            </div>
          </div>
          
          {selectedRole && (
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Role Properties */}
              <div className="bg-[#FAFAFA] border border-[#E0E0E0] rounded-lg p-4 mb-6">
                <h3 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#5B2C93]" />
                  {t("users.roleProperties")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="roleName" className="text-sm font-medium text-[#2C2C2C]">
                      {t("users.roleName")} <span className="text-[#DC2626]">*</span>
                    </Label>
                    <Input id="roleName" value={selectedRole.name}
                      onChange={(e) => setSelectedRole({...selectedRole, name: e.target.value})}
                      className="h-9 bg-white" placeholder={t("users.enterRoleName")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="roleDescription" className="text-sm font-medium text-[#2C2C2C]">{t("users.roleDescription")}</Label>
                    <Input id="roleDescription" value={selectedRole.description || ""}
                      onChange={(e) => setSelectedRole({...selectedRole, description: e.target.value})}
                      className="h-9 bg-white" placeholder={t("users.roleDescriptionPlaceholder")} />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-sm font-medium text-[#2C2C2C]">
                      {t("users.mobileAppRole")} <span className="text-[#DC2626]">*</span>
                    </Label>
                    <p className="text-xs text-[#6B6B6B] mb-1">{t("users.mobileAppRoleDescription")}</p>
                    <Select
                      value={selectedRole.mobileRole || 'employee'}
                      onValueChange={(v) => setSelectedRole({...selectedRole, mobileRole: v})}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder={t("users.selectMobileRole")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">{t("users.mobileRoleOptions.employee")}</SelectItem>
                        <SelectItem value="manager">{t("users.mobileRoleOptions.manager")}</SelectItem>
                        <SelectItem value="security">{t("users.mobileRoleOptions.security")}</SelectItem>
                        <SelectItem value="visitor">{t("users.mobileRoleOptions.visitor")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Authorization Objects */}
              <div className="border border-[#E0E0E0] rounded-lg overflow-hidden">
                <div className="bg-[#5B2C93] text-white px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                  <h3 className="text-xs font-medium uppercase tracking-wider flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    {t("users.authorizationObjects")}
                  </h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary"
                      className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
                      onClick={() => {
                        const allPerms: Record<string, Record<string, boolean>> = {};
                        permissionModules.forEach(m => {
                          allPerms[m.id] = {};
                          m.actions.forEach(a => { allPerms[m.id][a.id] = true; });
                        });
                        setSelectedRole({...selectedRole, permissions: allPerms});
                      }}>{t("common.selectAll")}</Button>
                    <Button size="sm" variant="secondary"
                      className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
                      onClick={() => setSelectedRole({...selectedRole, permissions: {}})}>{t("common.clear")}</Button>
                  </div>
                </div>
                
                <div className="divide-y divide-[#F0F0F0]">
                  {permissionModules.map((module, idx) => {
                    const modulePerms = selectedRole.permissions?.[module.id] || {};
                    const allChecked = module.actions.every(a => modulePerms[a.id]);
                    const someChecked = module.actions.some(a => modulePerms[a.id]);
                    
                    return (
                      <div key={module.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}>
                        <div className="flex items-center px-4 py-2.5 border-b border-[#F0F0F0]">
                          <div className="flex items-center gap-3 flex-1">
                            <Checkbox id={`module-${module.id}`} checked={allChecked}
                              className={someChecked && !allChecked ? 'data-[state=checked]:bg-[#E8DCF5]' : ''}
                              onCheckedChange={(checked) => {
                                const newModulePerms: Record<string, boolean> = {};
                                module.actions.forEach(a => { newModulePerms[a.id] = !!checked; });
                                setSelectedRole((prev: any) => ({
                                  ...prev,
                                  permissions: { ...prev.permissions, [module.id]: newModulePerms }
                                }));
                              }} />
                            <span className="font-medium text-sm text-[#2C2C2C]">{module.label}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F5F5F5] text-[#6B6B6B]">
                              {module.actions.filter(a => modulePerms[a.id]).length}/{module.actions.length}
                            </span>
                          </div>
                        </div>
                        <div className="px-4 py-3 pl-10">
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                            {module.actions.map((action) => (
                              <div key={action.id} className="flex items-center gap-2">
                                <Checkbox id={`${module.id}-${action.id}`}
                                  checked={modulePerms[action.id] || false}
                                  onCheckedChange={(checked) => {
                                    setSelectedRole((prev: any) => ({
                                      ...prev,
                                      permissions: {
                                        ...prev.permissions,
                                        [module.id]: { ...prev.permissions?.[module.id], [action.id]: checked }
                                      }
                                    }));
                                  }}
                                  className="h-4 w-4" />
                                <Label htmlFor={`${module.id}-${action.id}`}
                                  className="text-sm text-[#2C2C2C] cursor-pointer hover:text-[#5B2C93] transition-colors">
                                  {action.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#E0E0E0] bg-[#FAFAFA] flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-[#6B6B6B]">
              {selectedRole && (
                <span>
                  {Object.values(selectedRole.permissions || {}).reduce((acc: number, mod: any) => 
                    acc + Object.values(mod || {}).filter(Boolean).length, 0
                  )} {t("users.permissionsSelected")}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setEditRoleOpen(false)}>{t("common.cancel")}</Button>
              <Button className="bg-[#5B2C93] hover:bg-[#3D1C5E] min-w-[120px] sm:min-w-[140px]"
                onClick={handleSaveRole}
                disabled={createRoleMutation.isPending || updateRoleMutation.isPending}>
                {(createRoleMutation.isPending || updateRoleMutation.isPending) && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                <Save className="h-4 w-4 mr-2" />
                {selectedRole?.isNew ? t("users.createRole") : t("common.saveChanges")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {t("users.deleteUser")}
            </DialogTitle>
            <DialogDescription>
              <p>
                {t("users.deleteUserConfirm")}{" "}
                <strong>{deleteUserTarget?.name || deleteUserTarget?.email}</strong>.
                {" "}{t("users.deleteUserWarning")}
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="admin-delete-confirm" className="text-sm font-medium">
              {t("users.typeDeleteToConfirm")} <span className="font-mono font-bold text-red-600">DELETE</span>
            </Label>
            <Input
              id="admin-delete-confirm"
              placeholder="DELETE"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="border-red-200 focus-visible:ring-red-400"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteUserOpen(false)}
              disabled={deleteUserMutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteUser}
              disabled={deleteConfirmText !== "DELETE" || deleteUserMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteUserMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("users.deleting")}</>
              ) : (
                <><Trash2 className="mr-2 h-4 w-4" /> {t("users.deleteUser")}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
